import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { releaseModuleForUrl } from '@constant/remmi-release-modules';
import { ModuleNotifications } from '@models/remmi-release.model';
import { RemmiReleasesService } from '@services/api/remmi-releases.service';
import { combineLatest, Subscription } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

/**
 * Compact "Feature | …  [View now] [Ignore] ×" banner (design D2), placed under a page's
 * breadcrumb / tab strip. One banner per module, never stacked: several pending releases collapse
 * into the backend's "N new features available" group banner.
 *
 * Without a `module` input the module is worked out from the URL on every navigation, so a single
 * banner in a shared shell (the project edit tabs) follows the tab — Leads tab → `leads`,
 * Reporting tab → `reports`, every other tab → `projects`.
 */
@Component({
  selector: 'app-feature-release-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './feature-release-banner.component.html',
  styleUrls: ['./feature-release-banner.component.scss'],
})
export class FeatureReleaseBannerComponent implements OnInit, OnDestroy {
  /** Release module key; omit to resolve it from the URL. */
  @Input() module: string;
  /**
   * For components that are also routed inside the project edit shell (e.g. the calendar): the shell
   * already shows its own banner there, so this one stands down rather than stacking a second.
   */
  @Input() hideInProjectShell = false;

  entry: ModuleNotifications = null;
  /** × hides the banner for this page visit only — it is not recorded, so it is back next visit. */
  private dismissedPath: string = null;
  private currentPath: string = null;
  private subscription: Subscription;

  constructor(
    private releasesService: RemmiReleasesService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.releasesService.ensureLoaded();

    const path$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.router.url)
    );

    this.subscription = combineLatest([this.releasesService.notifications$, path$]).subscribe(([modules, url]) => {
      const path = (url ?? '').split(/[?#;]/)[0];
      if (path !== this.currentPath) {
        this.currentPath = path;
        this.dismissedPath = null;
      }
      const key = this.hideInProjectShell && /^\/projects\/edit_\//i.test(path) ? null : this.module || releaseModuleForUrl(url);
      this.entry = key ? this.releasesService.forModule(modules, key) : null;
      // Some hosts (the chat page) are OnPush, so an update arriving from the store must flag this view.
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get isVisible(): boolean {
    return !!this.entry?.banner && this.dismissedPath !== this.currentPath;
  }

  get isGroup(): boolean {
    return this.entry?.banner?.mode === 'group';
  }

  /** Single: the window records the view itself, which also drops the banner. Group: list mode. */
  viewNow(): void {
    const banner = this.entry?.banner;
    if (!banner) {
      return;
    }
    if (this.isGroup) {
      this.releasesService.openWindow({ mode: 'list', releases: this.entry.releases });
      return;
    }
    this.releasesService.openWindow({ mode: 'single', releaseId: banner.release_id });
  }

  /** Permanent. The service drops the release(s) locally first, so the banner goes before the call returns. */
  ignore(): void {
    const banner = this.entry?.banner;
    if (!banner) {
      return;
    }
    const request = this.isGroup
      ? this.releasesService.ignoreMany(banner.release_ids ?? this.entry.releases.map(release => release._id), 'grouped_banner')
      : this.releasesService.ignore(banner.release_id, 'banner');
    request.subscribe({ error: () => {} });
  }

  dismiss(): void {
    this.dismissedPath = this.currentPath;
  }
}
